/**
 * Cluster Layer Configuration
 *
 * Provides sensor clustering using Supercluster library for efficient
 * point aggregation with MapLibre GL JS visualization.
 *
 * Features:
 * - Supercluster-based point clustering
 * - Graduated cluster sizes based on point count
 * - Color-coded clusters by average moisture
 * - Click to expand/zoom into clusters
 * - Configurable cluster radius and maxZoom
 * - Smooth unclustered point display at high zoom
 *
 * @see https://github.com/mapbox/supercluster
 * @see https://maplibre.org/maplibre-style-spec/layers/#circle
 */

import Supercluster from 'supercluster';
import type { PointFeature } from 'supercluster';
import type { CircleLayerSpecification, SymbolLayerSpecification, ExpressionSpecification } from 'maplibre-gl';
import type {
  ClusterLayerConfig,
  SensorDataPoint,
  SensorFeature,
  ClusterPoint,
} from '../MapComponent.types';

// ============================================================================
// Supercluster Configuration
// ============================================================================

/**
 * Create a new Supercluster instance configured for sensor data
 *
 * @param config - Cluster layer configuration
 * @returns Configured Supercluster instance
 */
export const createSupercluster = (
  config: ClusterLayerConfig
): Supercluster<SensorFeature['properties'], ClusterPoint['properties']> => {
  return new Supercluster({
    radius: config.radius,
    maxZoom: config.maxZoom,
    minZoom: 0,
    minPoints: 2,
    // Reduce function to calculate average moisture for clusters
    map: (props) => ({
      moisture: props.moisture || 0,
      count: 1,
    }),
    reduce: (accumulated, props) => {
      // Calculate weighted average moisture
      const totalCount = accumulated.count + props.count;
      accumulated.moisture =
        (accumulated.moisture * accumulated.count + props.moisture * props.count) / totalCount;
      accumulated.count = totalCount;
    },
  });
};

/**
 * Load sensor data into Supercluster
 *
 * @param cluster - Supercluster instance
 * @param sensors - Array of sensor data points
 */
export const loadClusterData = (
  cluster: Supercluster<SensorFeature['properties'], ClusterPoint['properties']>,
  sensors: SensorDataPoint[]
): void => {
  const features: GeoJSON.Feature<GeoJSON.Point, SensorFeature['properties']>[] = sensors.map(sensor => ({
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [sensor.longitude, sensor.latitude],
    },
    properties: {
      id: sensor.id,
      deviceId: sensor.deviceId,
      moisture: sensor.moistureLevel,
      temperature: sensor.temperature,
      zone: sensor.zone,
      timestamp: typeof sensor.timestamp === 'string'
        ? sensor.timestamp
        : sensor.timestamp?.toISOString?.() || new Date().toISOString(),
    },
  }));

  cluster.load(features);
};

/**
 * Get clusters for the current viewport
 *
 * @param cluster - Supercluster instance
 * @param bounds - Map viewport bounds [westLng, southLat, eastLng, northLat]
 * @param zoom - Current zoom level
 * @returns Array of cluster and unclustered point features
 */
export const getClusters = (
  cluster: Supercluster<SensorFeature['properties'], ClusterPoint['properties']>,
  bounds: [number, number, number, number],
  zoom: number
): ClusterPoint[] => {
  if (!Number.isFinite(zoom) || bounds.some(b => !Number.isFinite(b))) {
    return [];
  }
  const clusters = cluster.getClusters(bounds, Math.floor(zoom));
  return clusters as ClusterPoint[];
};

/**
 * Get leaf points (original sensors) from a cluster
 *
 * @param cluster - Supercluster instance
 * @param clusterId - Cluster ID to expand
 * @param limit - Maximum number of leaves to return
 * @returns Array of original sensor features
 */
export const getClusterLeaves = (
  cluster: Supercluster<SensorFeature['properties'], ClusterPoint['properties']>,
  clusterId: number,
  limit: number = 100
): SensorFeature[] => {
  const leaves = cluster.getLeaves(clusterId, limit);
  return leaves as unknown as SensorFeature[];
};

/**
 * Get expansion zoom level for a cluster
 *
 * @param cluster - Supercluster instance
 * @param clusterId - Cluster ID
 * @returns Zoom level at which cluster expands
 */
export const getClusterExpansionZoom = (
  cluster: Supercluster<SensorFeature['properties'], ClusterPoint['properties']>,
  clusterId: number
): number => {
  return cluster.getClusterExpansionZoom(clusterId);
};

// ============================================================================
// Color Configuration
// ============================================================================

/**
 * Cluster color expression based on average moisture
 */
export const clusterColorExpression: ExpressionSpecification = [
  'case',
  ['has', 'point_count'],
  // For clusters, use the accumulated moisture value
  [
    'interpolate',
    ['linear'],
    ['get', 'moisture'],
    0, '#B26640',    // brown (dry)
    30, '#DEB464',   // tan
    50, '#41AB5D',   // green (optimal)
    70, '#67A9CF',   // light blue
    100, '#2166AC', // blue (saturated)
  ],
  // For unclustered points, use the moisture property
  [
    'interpolate',
    ['linear'],
    ['get', 'moisture'],
    0, '#B26640',
    30, '#DEB464',
    50, '#41AB5D',
    70, '#67A9CF',
    100, '#2166AC',
  ],
];

// ============================================================================
// Size Configuration
// ============================================================================

/**
 * Cluster size expression based on point count
 * Clusters grow larger with more contained points
 */
export const clusterSizeExpression: ExpressionSpecification = [
  'step',
  ['get', 'point_count'],
  15,   // Default size for 2-9 points
  10, 20,   // 10-24 points
  25, 25,   // 25-49 points
  50, 30,   // 50-99 points
  100, 35,  // 100+ points
];

/**
 * Unclustered point size (individual sensors)
 */
export const unclusteredPointSize = 8;

// ============================================================================
// Paint Configuration
// ============================================================================

/**
 * Get cluster circle paint configuration
 *
 * @returns MapLibre circle paint specification for clusters
 */
export const getClusterPaint = (): CircleLayerSpecification['paint'] => {
  return {
    'circle-radius': clusterSizeExpression,
    'circle-color': clusterColorExpression,
    'circle-stroke-width': 3,
    'circle-stroke-color': '#ffffff',
    'circle-opacity': 0.9,
  } as CircleLayerSpecification['paint'];
};

/**
 * Get unclustered point paint configuration
 *
 * @returns MapLibre circle paint specification for individual points
 */
export const getUnclusteredPointPaint = (): CircleLayerSpecification['paint'] => ({
  'circle-radius': unclusteredPointSize,
  'circle-color': [
    'interpolate',
    ['linear'],
    ['get', 'moisture'],
    0, '#B26640',
    30, '#DEB464',
    50, '#41AB5D',
    70, '#67A9CF',
    100, '#2166AC',
  ] as ExpressionSpecification,
  'circle-stroke-width': 2,
  'circle-stroke-color': '#ffffff',
  'circle-opacity': 0.9,
});

/**
 * Get cluster count text paint configuration
 *
 * @returns MapLibre symbol paint specification for cluster count
 */
export const getClusterCountPaint = (): SymbolLayerSpecification['paint'] => ({
  'text-color': '#ffffff',
});

/**
 * Get cluster count text layout configuration
 *
 * @returns MapLibre symbol layout specification for cluster count
 */
export const getClusterCountLayout = (): SymbolLayerSpecification['layout'] => ({
  'text-field': [
    'case',
    ['>=', ['get', 'point_count'], 1000],
    ['concat', ['/', ['round', ['/', ['get', 'point_count'], 100]], 10], 'k'],
    ['to-string', ['get', 'point_count']],
  ] as ExpressionSpecification,
  'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
  'text-size': 12,
  'text-allow-overlap': true,
});

// ============================================================================
// Layer IDs
// ============================================================================

export const CLUSTER_LAYER_ID = 'sensor-clusters';
export const CLUSTER_COUNT_LAYER_ID = 'sensor-cluster-count';
export const UNCLUSTERED_POINT_LAYER_ID = 'sensor-unclustered-point';

// ============================================================================
// Default Configuration
// ============================================================================

/**
 * Default cluster layer configuration
 */
export const defaultClusterConfig: ClusterLayerConfig = {
  enabled: false,
  radius: 50,
  maxZoom: 14,
};

// ============================================================================
// Layer Specifications
// ============================================================================

/**
 * Get cluster circles layer specification
 *
 * @param sourceId - Source ID for the GeoJSON data
 * @returns Full circle layer specification for clusters
 */
export const getClusterLayerSpec = (
  sourceId: string
): Omit<CircleLayerSpecification, 'source-layer'> => ({
  id: CLUSTER_LAYER_ID,
  type: 'circle',
  source: sourceId,
  filter: ['has', 'point_count'],
  paint: getClusterPaint(),
});

/**
 * Get cluster count labels layer specification
 *
 * @param sourceId - Source ID for the GeoJSON data
 * @returns Full symbol layer specification for cluster counts
 */
export const getClusterCountLayerSpec = (
  sourceId: string
): Omit<SymbolLayerSpecification, 'source-layer'> => ({
  id: CLUSTER_COUNT_LAYER_ID,
  type: 'symbol',
  source: sourceId,
  filter: ['has', 'point_count'],
  paint: getClusterCountPaint(),
  layout: getClusterCountLayout(),
});

/**
 * Get unclustered points layer specification
 *
 * @param sourceId - Source ID for the GeoJSON data
 * @returns Full circle layer specification for unclustered points
 */
export const getUnclusteredPointLayerSpec = (
  sourceId: string
): Omit<CircleLayerSpecification, 'source-layer'> => ({
  id: UNCLUSTERED_POINT_LAYER_ID,
  type: 'circle',
  source: sourceId,
  filter: ['!', ['has', 'point_count']],
  paint: getUnclusteredPointPaint(),
});

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Convert sensors to GeoJSON for MapLibre clustering source
 *
 * @param sensors - Array of sensor data points
 * @param clusterConfig - Cluster configuration
 * @returns GeoJSON source configuration with clustering enabled
 */
export const getSensorClusterSource = (
  sensors: SensorDataPoint[],
  clusterConfig: ClusterLayerConfig
): GeoJSON.FeatureCollection<GeoJSON.Point> & {
  cluster: boolean;
  clusterRadius: number;
  clusterMaxZoom: number;
  clusterProperties: Record<string, ExpressionSpecification>;
} => {
  const features: GeoJSON.Feature<GeoJSON.Point>[] = sensors.map(sensor => ({
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [sensor.longitude, sensor.latitude],
    },
    properties: {
      id: sensor.id,
      deviceId: sensor.deviceId,
      moisture: sensor.moistureLevel,
      temperature: sensor.temperature,
      zone: sensor.zone,
      timestamp: typeof sensor.timestamp === 'string'
        ? sensor.timestamp
        : sensor.timestamp?.toISOString?.() || new Date().toISOString(),
    },
  }));

  return {
    type: 'FeatureCollection',
    features,
    cluster: true,
    clusterRadius: clusterConfig.radius,
    clusterMaxZoom: clusterConfig.maxZoom,
    clusterProperties: {
      moisture: ['+', ['get', 'moisture']],
    },
  };
};

/**
 * Calculate average moisture for a cluster
 *
 * @param pointCount - Number of points in cluster
 * @param sumMoisture - Sum of moisture values
 * @returns Average moisture percentage
 */
export const calculateClusterAvgMoisture = (
  pointCount: number,
  sumMoisture: number
): number => {
  if (pointCount === 0) return 0;
  return Math.round((sumMoisture / pointCount) * 10) / 10;
};

export default {
  createSupercluster,
  loadClusterData,
  getClusters,
  getClusterLeaves,
  getClusterExpansionZoom,
  getClusterPaint,
  getClusterLayerSpec,
  getClusterCountLayerSpec,
  getUnclusteredPointLayerSpec,
  getSensorClusterSource,
  calculateClusterAvgMoisture,
  defaultClusterConfig,
  CLUSTER_LAYER_ID,
  CLUSTER_COUNT_LAYER_ID,
  UNCLUSTERED_POINT_LAYER_ID,
  clusterSizeExpression,
  clusterColorExpression,
};
